import { createContext, use } from 'react';
import { queryOptions } from '@tanstack/react-query';
import { publicApi } from '@/lib/api/public';
import { publicTenantSchema, type PublicTenant } from '@/lib/api/schemas';
import { readJson, writeJson } from '@/lib/safe-storage';

const cacheKey = (slug: string) => `plansgx:${slug}:studio`;

interface CachedStudio {
  savedAt: number;
  data: unknown;
}

/** Last public studio card, so services, prices and contacts open without network. */
function readCachedStudio(slug: string): { data: PublicTenant; savedAt: number } | undefined {
  const cached = readJson<CachedStudio | null>(cacheKey(slug), null);
  if (!cached) return undefined;
  const parsed = publicTenantSchema.safeParse(cached.data);
  return parsed.success ? { data: parsed.data, savedAt: cached.savedAt } : undefined;
}

export const studioQuery = (slug: string) =>
  queryOptions({
    queryKey: ['studio', slug],
    queryFn: async () => {
      const data = await publicApi.tenant(slug);
      writeJson(cacheKey(slug), { savedAt: Date.now(), data } satisfies CachedStudio);
      return data;
    },
    initialData: () => readCachedStudio(slug)?.data,
    initialDataUpdatedAt: () => readCachedStudio(slug)?.savedAt,
    staleTime: 60_000,
  });

export const StudioContext = createContext<PublicTenant | null>(null);

export function useStudio(): PublicTenant {
  const studio = use(StudioContext);
  if (!studio) throw new Error('useStudio outside of a studio route');
  return studio;
}
